/**
 * 네이티브 Google 로그인 서비스
 * React Native WebView 브릿지를 통해 네이티브 Google 로그인 SDK 호출
 */


export interface NativeGoogleAuthResult {
  idToken: string
  email: string
  name: string
  imageUrl?: string
}

class NativeGoogleAuthService {
  private isInitialized = false

  /**
   * React Native WebView 브릿지 사용 가능 여부 확인
   */
  private isBridgeAvailable(): boolean {
    return typeof window !== 'undefined' && 
           (window.ReactNativeWebView !== undefined || 
            (window as any).webkit?.messageHandlers?.googleLogin !== undefined ||
            (window as any).Android?.googleLogin !== undefined)
  }

  /**
   * 초기화 (필요시)
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return
    
    if (!this.isBridgeAvailable()) {
      throw new Error('네이티브 Google 로그인을 사용할 수 없습니다. 앱에서 실행해주세요.')
    }
    
    this.isInitialized = true
  }

  /**
   * Google 로그인 실행
   */
  async signIn(): Promise<NativeGoogleAuthResult> {
    await this.initialize()

    return new Promise((resolve, reject) => {
      // 타임아웃 설정 (60초)
      const timeout = setTimeout(() => {
        if ((window as any).googleLoginCallback) {
          delete (window as any).googleLoginCallback
        }
        reject(new Error('Google 로그인 시간이 초과되었습니다. 다시 시도해주세요.'))
      }, 60000)

      // 로그인 완료 콜백 등록
      ;(window as any).googleLoginCallback = (result: NativeGoogleAuthResult | null, error: string | null) => {
        clearTimeout(timeout)
        delete (window as any).googleLoginCallback

        if (error) {
          reject(new Error(error))
        } else if (result && result.idToken) {
          resolve(result)
        } else {
          reject(new Error('Google 로그인에서 idToken을 받지 못했습니다.'))
        }
      }

      // React Native WebView 브릿지 사용
      if (window.ReactNativeWebView) {
        const message = JSON.stringify({
          type: 'GOOGLE_LOGIN',
        })
        window.ReactNativeWebView.postMessage(message)
      }
      // iOS WebKit 메시지 핸들러 (fallback)
      else if (
        (window as any).webkit &&
        (window as any).webkit.messageHandlers &&
        (window as any).webkit.messageHandlers.googleLogin
      ) {
        ;(window as any).webkit.messageHandlers.googleLogin.postMessage({})
      }
      // Android WebView JavaScript Interface (fallback)
      else if ((window as any).Android && typeof (window as any).Android.googleLogin === 'function') {
        ;(window as any).Android.googleLogin()
      }
      else {
        clearTimeout(timeout)
        delete (window as any).googleLoginCallback
        reject(new Error('Google 로그인 브릿지를 사용할 수 없습니다. 앱에서 실행해주세요.'))
      }
    })
  }

  /**
   * Google 로그아웃
   */
  async signOut(): Promise<void> {
    if (!this.isBridgeAvailable()) {
      return
    }

    // React Native WebView 브릿지 사용
    if (window.ReactNativeWebView) {
      const message = JSON.stringify({
        type: 'GOOGLE_LOGOUT',
      })
      window.ReactNativeWebView.postMessage(message)
    }
    // iOS WebKit 메시지 핸들러 (fallback)
    else if (
      (window as any).webkit &&
      (window as any).webkit.messageHandlers &&
      (window as any).webkit.messageHandlers.googleLogout
    ) {
      ;(window as any).webkit.messageHandlers.googleLogout.postMessage({})
    }
    // Android WebView JavaScript Interface (fallback)
    else if ((window as any).Android && typeof (window as any).Android.googleLogout === 'function') {
      ;(window as any).Android.googleLogout()
    }
  }
}

export const nativeGoogleAuthService = new NativeGoogleAuthService()
